/**
 * Keyboard Shortcuts System
 *
 * Defines the canonical shortcut map for Stream. Provides platform
 * detection, display formatting, and event matching utilities.
 *
 * Architecture:
 *   - Single source of truth for all shortcuts
 *   - Platform-aware display (Cmd on Mac, Ctrl on Windows/Linux)
 *   - Scoped shortcuts that only fire in the right context
 *   - Input suppression for single-letter shortcuts
 *   - Two-key chord state machine (`g <letter>`) backed by the shortcut store
 */

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type ShortcutScope =
  | "global"
  | "queue"
  | "approval"
  | "rejectDialog"
  | "sessionPanel"
  | "health"
  | "events"
  | "engagement"
  | "customers"
  | "sessions"
  | "agent-audit"
  | "automations"
  | "playground"
  | "inbox"
  | "catalog"
  | "purchases"
  | "email"
  | "agent-cost"
  | "deferred"
  | "eng-dashboard"
  | "artifacts"
  | "integrations"
  | "email-health";

export interface ShortcutDefinition {
  /** Unique identifier for the shortcut. */
  id: string;
  /** Human-readable description of the action. */
  description: string;
  /**
   * The key(s) that trigger this shortcut.
   *
   * - Single character: `"j"` — matches the bare key.
   * - Named key: `"Enter"`, `"Escape"`, `"Tab"`.
   * - Two-key chord: `"g e"` (space-separated) — fires only when the chord
   *   prefix `g` was pressed within the last 1000ms and the follower key
   *   matches.
   */
  key: string;
  /** Whether Cmd (Mac) or Ctrl (Windows/Linux) is required. */
  mod?: boolean;
  /** Whether Shift is required. */
  shift?: boolean;
  /** Whether Alt/Option is required. */
  alt?: boolean;
  /** The scope in which this shortcut is active. */
  scope: ShortcutScope;
  /** Whether this shortcut should fire even when in an editable element. */
  ignoreInputFocus?: boolean;
}

export interface ShortcutGroup {
  scope: ShortcutScope;
  label: string;
  shortcuts: ShortcutDefinition[];
}

// -----------------------------------------------------------------------------
// Platform Detection
// -----------------------------------------------------------------------------

let cachedIsMac: boolean | null = null;

/**
 * Detect if the current platform is macOS.
 *
 * Cached for performance. Returns false during SSR.
 */
export function isMac(): boolean {
  if (cachedIsMac !== null) return cachedIsMac;
  if (typeof window === "undefined") return false;
  cachedIsMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  return cachedIsMac;
}

/**
 * Reset the cached platform detection (for testing).
 */
export function resetPlatformCache(): void {
  cachedIsMac = null;
}

// -----------------------------------------------------------------------------
// Shortcut Definitions
// -----------------------------------------------------------------------------

/**
 * Global shortcuts - always active unless a modal captures input.
 */
const GLOBAL_SHORTCUTS: ShortcutDefinition[] = [
  {
    id: "command-palette",
    description: "Open command palette",
    key: "k",
    mod: true,
    scope: "global",
    ignoreInputFocus: true,
  },
  {
    id: "shortcuts-overlay",
    description: "Show keyboard shortcuts",
    key: "/",
    mod: true,
    scope: "global",
    ignoreInputFocus: true,
  },
  {
    id: "shortcuts-overlay-bare",
    description: "Show keyboard shortcuts (bare ?)",
    key: "?",
    scope: "global",
  },
  {
    id: "toggle-session-panel",
    description: "Toggle session panel",
    key: "\\",
    mod: true,
    scope: "global",
    ignoreInputFocus: true,
  },
  // ── Two-key chord global navigation (`g <letter>`) ─────────────────────────
  // Per STREAM_UX_PATTERNS.md §7.1. Conflict-resolution comments inline.
  {
    id: "go-to-events",
    description: "Go to Events",
    key: "g e",
    scope: "global",
  },
  {
    id: "go-to-queue",
    description: "Go to Queue",
    key: "g q",
    scope: "global",
  },
  {
    id: "go-to-health",
    description: "Go to System Health",
    key: "g h",
    scope: "global",
  },
  {
    id: "go-to-customers",
    description: "Go to Customers",
    key: "g c",
    scope: "global",
  },
  {
    id: "go-to-agents",
    description: "Go to Agents",
    key: "g a",
    scope: "global",
  },
  {
    id: "go-to-sessions",
    description: "Go to Sessions",
    key: "g s",
    scope: "global",
  },
  {
    id: "go-to-automations",
    description: "Go to Automations",
    key: "g u",
    scope: "global",
  },
  {
    id: "go-to-inbox",
    description: "Go to Inbox",
    key: "g i",
    scope: "global",
  },
  {
    id: "go-to-catalog",
    description: "Go to Catalog",
    key: "g t",
    scope: "global",
  },
  {
    id: "go-to-purchases",
    description: "Go to Purchases",
    key: "g b",
    scope: "global",
  },
  {
    id: "go-to-email",
    description: "Go to Email Templates & Themes",
    key: "g m",
    scope: "global",
  },
  {
    id: "go-to-agent-cost",
    description: "Go to Agent Cost Tracking",
    // 'g $' is not reliably matchable as a single-key chord; use 'g d' (Dollar)
    // per task 2575 §7.7.
    key: "g d",
    scope: "global",
  },
  {
    id: "go-to-eng-dashboard",
    description: "Go to Engineering Dashboard",
    // Task 2571 §7.7 specifies 'g e' but that conflicts with 'go-to-events'.
    // Using 'g n' (eNgineering) as the next available chord.
    key: "g n",
    scope: "global",
  },
  {
    id: "go-to-deferred",
    description: "Go to Deferred Items Queue",
    // Task 2573 §7.7 specifies 'g d' but that conflicts with 'go-to-agent-cost'.
    // Using 'g w' (scheduled Work) as the next available chord.
    key: "g w",
    scope: "global",
  },
  {
    id: "go-to-artifacts",
    description: "Go to Artifacts",
    // Task 2572 §7.7 specifies 'g f' (artiFacts; 'a' taken by agents).
    key: "g f",
    scope: "global",
  },
  {
    id: "go-to-integrations",
    description: "Go to Integrations",
    // Task 2578 §7.7 specifies 'g i'; 'g i' is taken by Inbox (Task 2569).
    // Using 'g v' (Vendors) as the next available chord.
    key: "g v",
    scope: "global",
  },
  {
    id: "go-to-email-health",
    description: "Go to Email & Inbox Health",
    // Task 2579 §7.7 specifies 'g m' but that's taken by Email Templates.
    // Using 'g y' (deliverY) as the next available chord.
    key: "g y",
    scope: "global",
  },
];

/**
 * Queue shortcuts - active when queue has focus and no input is focused.
 */
const QUEUE_SHORTCUTS: ShortcutDefinition[] = [
  { id: "queue-next", description: "Next item", key: "j", scope: "queue" },
  { id: "queue-prev", description: "Previous item", key: "k", scope: "queue" },
  { id: "queue-expand", description: "Expand selected item", key: "Enter", scope: "queue" },
  { id: "queue-collapse", description: "Collapse / back to list", key: "Escape", scope: "queue" },
  { id: "queue-claim", description: "Claim selected item", key: "c", scope: "queue" },
];

const APPROVAL_SHORTCUTS: ShortcutDefinition[] = [
  { id: "approval-approve", description: "Approve", key: "a", scope: "approval" },
  { id: "approval-suppress", description: "Suppress", key: "s", scope: "approval" },
  { id: "approval-reject", description: "Open reject dialog", key: "r", scope: "approval" },
  { id: "approval-edit", description: "Edit (if supported)", key: "e", scope: "approval" },
];

const REJECT_DIALOG_SHORTCUTS: ShortcutDefinition[] = [
  { id: "reject-reason-1", description: "Toggle reason 1", key: "1", scope: "rejectDialog" },
  { id: "reject-reason-2", description: "Toggle reason 2", key: "2", scope: "rejectDialog" },
  { id: "reject-reason-3", description: "Toggle reason 3", key: "3", scope: "rejectDialog" },
  { id: "reject-reason-4", description: "Toggle reason 4", key: "4", scope: "rejectDialog" },
  {
    id: "reject-submit",
    description: "Submit rejection",
    key: "Enter",
    mod: true,
    scope: "rejectDialog",
    ignoreInputFocus: true,
  },
];

const SESSION_PANEL_SHORTCUTS: ShortcutDefinition[] = [
  {
    id: "session-send",
    description: "Send message",
    key: "Enter",
    mod: true,
    scope: "sessionPanel",
    ignoreInputFocus: true,
  },
  {
    id: "session-blur",
    description: "Blur input",
    key: "Escape",
    scope: "sessionPanel",
    ignoreInputFocus: true,
  },
];

// Stage-5 feature shortcut groups are registered by their feature pages via
// `getShortcutsForScope(...)` and use direct `matchesShortcut` calls. The
// scope strings are listed in `ShortcutScope` above so the registrations
// type-check; the actual key tables live in each feature's per-page handler.
// Empty placeholder groups keep the overlay listing consistent for scopes
// whose feature does not define a shortcut table here.

/**
 * Email Health (Mailgun Delivery) Dashboard shortcuts (Task 3110).
 * Dashboard archetype, sibling of 2555 System Health Dashboards.
 * Global navigate: 'g y' (deliverY; 'g m' is taken by Email Templates).
 */
const EMAIL_HEALTH_SHORTCUTS: ShortcutDefinition[] = [
  {
    id: "email-health-refresh",
    description: "Refresh all panels",
    key: "r",
    shift: true,
    scope: "email-health",
  },
  {
    id: "email-health-cycle-range",
    description: "Cycle time range presets",
    key: "t",
    scope: "email-health",
  },
  {
    id: "email-health-range-1h",
    description: "Set range to 1 hour",
    key: "1",
    scope: "email-health",
  },
  {
    id: "email-health-range-24h",
    description: "Set range to 24 hours",
    key: "2",
    scope: "email-health",
  },
  {
    id: "email-health-range-7d",
    description: "Set range to 7 days",
    key: "3",
    scope: "email-health",
  },
  {
    id: "email-health-range-30d",
    description: "Set range to 30 days",
    key: "4",
    scope: "email-health",
  },
  {
    id: "email-health-clear-filter",
    description: "Clear cross-filter",
    key: "Escape",
    scope: "email-health",
  },
];

/**
 * Integration Health shortcuts — active on the /integrations page (Task 2578).
 * Dashboard archetype, sibling of 2555 System Health Dashboards.
 * Global navigate: 'g v' (Vendors; spec wanted 'g i' but that's taken by Inbox).
 */
const INTEGRATIONS_SHORTCUTS: ShortcutDefinition[] = [
  {
    id: "integrations-refresh",
    description: "Refresh integration health",
    key: "r",
    scope: "integrations",
  },
  {
    id: "integrations-cycle-range",
    description: "Cycle time range presets",
    key: "t",
    scope: "integrations",
  },
  {
    id: "integrations-range-1h",
    description: "Set range to 1 hour",
    key: "1",
    scope: "integrations",
  },
  {
    id: "integrations-range-24h",
    description: "Set range to 24 hours",
    key: "2",
    scope: "integrations",
  },
  {
    id: "integrations-range-7d",
    description: "Set range to 7 days",
    key: "3",
    scope: "integrations",
  },
  {
    id: "integrations-range-30d",
    description: "Set range to 30 days",
    key: "4",
    scope: "integrations",
  },
  {
    id: "integrations-test-all",
    description: "Run synthetic tests against every test-supporting provider",
    key: "T",
    shift: true,
    scope: "integrations",
  },
  ...Array.from({ length: 9 }).map((_, i) => ({
    id: `integrations-focus-${i + 1}`,
    description: `Focus provider card ${i + 1}`,
    key: `${i + 1}`,
    scope: "integrations" as ShortcutScope,
  })),
  {
    id: "integrations-clear-filter",
    description: "Clear cross-filter and facet filters",
    key: "Escape",
    scope: "integrations",
  },
  {
    id: "integrations-close-drawer",
    description: "Close drawer",
    key: "Escape",
    scope: "integrations",
  },
  {
    id: "integrations-errors-next",
    description: "Next error row",
    key: "j",
    scope: "integrations",
  },
  {
    id: "integrations-errors-prev",
    description: "Previous error row",
    key: "k",
    scope: "integrations",
  },
];

/**
 * All shortcuts grouped by scope.
 */
export const SHORTCUT_GROUPS: ShortcutGroup[] = [
  { scope: "global", label: "Global", shortcuts: GLOBAL_SHORTCUTS },
  { scope: "queue", label: "Queue", shortcuts: QUEUE_SHORTCUTS },
  { scope: "approval", label: "Approval Card", shortcuts: APPROVAL_SHORTCUTS },
  { scope: "rejectDialog", label: "Reject Dialog", shortcuts: REJECT_DIALOG_SHORTCUTS },
  { scope: "sessionPanel", label: "Session Panel", shortcuts: SESSION_PANEL_SHORTCUTS },
  { scope: "email-health", label: "Mailgun Delivery", shortcuts: EMAIL_HEALTH_SHORTCUTS },
  { scope: "integrations", label: "Integration Health", shortcuts: INTEGRATIONS_SHORTCUTS },
];

/**
 * Flat list of all shortcuts.
 */
export const ALL_SHORTCUTS: ShortcutDefinition[] = SHORTCUT_GROUPS.flatMap(
  (group) => group.shortcuts,
);

/**
 * Get shortcuts for a specific scope.
 */
export function getShortcutsForScope(scope: ShortcutScope): ShortcutDefinition[] {
  return ALL_SHORTCUTS.filter((s) => s.scope === scope);
}

/**
 * Find a shortcut by its ID.
 */
export function getShortcutById(id: string): ShortcutDefinition | undefined {
  return ALL_SHORTCUTS.find((s) => s.id === id);
}

// -----------------------------------------------------------------------------
// Display Formatting
// -----------------------------------------------------------------------------

/**
 * Format a key for display based on platform.
 *
 * Examples:
 *   - "k" with mod=true on Mac -> "Cmd+K"
 *   - "k" with mod=true on Windows -> "Ctrl+K"
 *   - "Enter" -> "Enter"
 *   - "g e" -> "G E" (chord, displayed as space-separated keys)
 */
export function formatShortcut(shortcut: ShortcutDefinition): string {
  const parts: string[] = [];

  if (shortcut.mod) {
    parts.push(isMac() ? "Cmd" : "Ctrl");
  }
  if (shortcut.shift) {
    parts.push("Shift");
  }
  if (shortcut.alt) {
    parts.push(isMac() ? "Option" : "Alt");
  }

  // Format the key itself
  let key = shortcut.key;
  if (key.length === 1) {
    key = key.toUpperCase();
  } else if (key.includes(" ")) {
    // Chord: "g e" -> "G E"
    key = key.toUpperCase();
  }

  parts.push(key);
  return parts.join("+");
}

/**
 * Get the modifier symbol for display.
 */
export function getModSymbol(): string {
  return isMac() ? "⌘" : "Ctrl";
}

/**
 * Format a shortcut with symbols (for inline display).
 */
export function formatShortcutSymbol(shortcut: ShortcutDefinition): string {
  const parts: string[] = [];

  if (shortcut.mod) {
    parts.push(isMac() ? "⌘" : "Ctrl+");
  }
  if (shortcut.shift) {
    parts.push(isMac() ? "⇧" : "Shift+");
  }
  if (shortcut.alt) {
    parts.push(isMac() ? "⌥" : "Alt+");
  }

  // Format the key itself
  let key = shortcut.key;
  if (key.length === 1) {
    key = key.toUpperCase();
  } else if (key === "Enter") {
    key = isMac() ? "↩" : "Enter";
  } else if (key === "Escape") {
    key = "Esc";
  } else if (key === "Tab") {
    key = isMac() ? "⇥" : "Tab";
  } else if (key.includes(" ")) {
    key = key.toUpperCase();
  }

  parts.push(key);
  return parts.join("");
}

// -----------------------------------------------------------------------------
// Event Matching
// -----------------------------------------------------------------------------

/**
 * Check if an element is an editable input where single-letter shortcuts
 * should be suppressed.
 */
export function isEditableElement(element: Element | null): boolean {
  if (!element) return false;
  if (element instanceof HTMLInputElement) return true;
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLSelectElement) return true;
  if (element instanceof HTMLElement && element.isContentEditable) return true;
  return false;
}

// -----------------------------------------------------------------------------
// Chord prefix provider (dependency-injected to avoid circular import with
// the shortcut store). The shortcut store registers its `chordPrefix` getter
// here at module-init time so `matchesShortcut` callers don't have to thread
// chord state through every per-page keyboard handler.
// -----------------------------------------------------------------------------

let chordPrefixProvider: () => string | null = () => null;

/**
 * Register a function that returns the currently active chord prefix.
 * Called once by the shortcut store at app startup.
 */
export function registerChordPrefixProvider(
  provider: () => string | null,
): void {
  chordPrefixProvider = provider;
}

/**
 * Read the currently active chord prefix from the registered provider.
 */
export function getActiveChordPrefix(): string | null {
  return chordPrefixProvider();
}

/**
 * Check if a keyboard event matches a shortcut definition.
 *
 * @param chordPrefix - The currently active chord prefix (e.g. "g") or
 *   `undefined` to read from the registered provider
 *   (`registerChordPrefixProvider`). When non-null, only chord shortcuts
 *   (`"g e"`) and modifier shortcuts can match — bare single-letter
 *   shortcuts are blocked so per-page handlers (`j`, `k`, `r`, …) do not
 *   race the global `g <letter>` navigation handlers. Esc still matches so
 *   the chord can be cancelled.
 */
export function matchesShortcut(
  event: KeyboardEvent,
  shortcut: ShortcutDefinition,
  chordPrefix: string | null | undefined = undefined,
): boolean {
  const effectiveChord =
    chordPrefix === undefined ? chordPrefixProvider() : chordPrefix;

  // Handle chord shortcuts (e.g., "g e")
  if (shortcut.key.includes(" ")) {
    const [prefix, follower] = shortcut.key.split(" ", 2);
    if (
      effectiveChord === prefix &&
      event.key.toLowerCase() === follower.toLowerCase()
    ) {
      return true;
    }
    return false;
  }

  // While a chord prefix is active, suppress bare-key shortcuts so they don't
  // race chord shortcuts. Allow Escape (cancel) and modifier shortcuts (the
  // chord handler clears the prefix on modifiers anyway).
  if (effectiveChord) {
    const hasModifier = shortcut.mod || shortcut.alt;
    if (!hasModifier && shortcut.key !== "Escape") return false;
  }

  // Handle bare "?" as special case (shift+/ on US layout, but we match event.key === '?').
  // Only matches when the shortcut definition also expects no required modifiers.
  if (shortcut.key === "?" && event.key === "?") {
    return !shortcut.mod && !shortcut.alt;
  }

  // Check modifiers
  const modRequired = shortcut.mod ?? false;
  const shiftRequired = shortcut.shift ?? false;
  const altRequired = shortcut.alt ?? false;

  const modPressed = event.metaKey || event.ctrlKey;
  const shiftPressed = event.shiftKey;
  const altPressed = event.altKey;

  if (modRequired !== modPressed) return false;
  if (shiftRequired !== shiftPressed) return false;
  if (altRequired !== altPressed) return false;

  // Check key
  // Handle special keys
  if (shortcut.key === "Enter" && event.key === "Enter") return true;
  if (shortcut.key === "Escape" && event.key === "Escape") return true;
  if (shortcut.key === "Tab" && event.key === "Tab") return true;

  // Handle single-character keys (case-insensitive)
  if (shortcut.key.length === 1) {
    return event.key.toLowerCase() === shortcut.key.toLowerCase();
  }

  // Direct key match
  return event.key === shortcut.key;
}

/**
 * Determine if a shortcut should be suppressed based on current focus.
 *
 * Single-letter shortcuts (no modifier) are suppressed when focus is in an
 * editable element. Modifier shortcuts always fire.
 */
export function shouldSuppressShortcut(
  event: KeyboardEvent,
  shortcut: ShortcutDefinition,
): boolean {
  // Never suppress if the shortcut explicitly ignores input focus
  if (shortcut.ignoreInputFocus) return false;

  // Never suppress shortcuts with modifiers
  if (shortcut.mod || shortcut.alt) return false;

  // Suppress single-letter shortcuts when in editable elements
  if (shortcut.key.length === 1 || shortcut.key === "Enter" || shortcut.key === "Escape") {
    return isEditableElement(event.target as Element);
  }

  return false;
}
